/**
 * TeamStrength Hub - Studio Dashboard Page
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MessagingCenter from '../components/MessagingCenter';
import { 
  Users, Layers, BarChart3, Target, TrendingUp, AlertTriangle,
  CheckCircle2, XCircle, Clock, LayoutDashboard, Mail
} from 'lucide-react';

const StudioPage = () => {
  const { currentOrg, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const fetchData = async () => {
      if (!currentOrg) return;
      
      try {
        setLoading(true);
        const response = await studioApi.getDashboard(currentOrg.org_id);
        setData(response.data);
      } catch (error) {
        toast.error('Failed to load studio data');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentOrg]);

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Select an organization to view the Studio</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 skeleton rounded-xl" />
          <div className="h-64 skeleton rounded-xl" />
        </div>
      </div>
    );
  }

  const { license_consumption, data_completeness, strength_distribution, team_comparison } = data || {};

  // Translations
  const lang = user?.preferred_language || 'de';
  const txt = {
    de: {
      dashboard: 'Dashboard',
      messaging: 'Messaging',
      selectOrg: 'Wähle eine Organisation für das Studio',
      orgInsights: 'Organisations-Einblicke und Analysen für'
    },
    en: {
      dashboard: 'Dashboard',
      messaging: 'Messaging',
      selectOrg: 'Select an organization to view the Studio',
      orgInsights: 'Organization-wide insights and analytics for'
    }
  }[lang];

  return (
    <div className="space-y-6" data-testid="studio-page">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
          Studio
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {txt.orgInsights} {currentOrg.name}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <LayoutDashboard className="w-4 h-4" />
            {txt.dashboard}
          </TabsTrigger>
          <TabsTrigger value="messaging" className="gap-2">
            <Mail className="w-4 h-4" />
            {txt.messaging}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          {loading ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-32 skeleton rounded-xl" />
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-64 skeleton rounded-xl" />
                <div className="h-64 skeleton rounded-xl" />
              </div>
            </div>
          ) : (
            <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-fade-in stagger-1">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">Active Teams</p>
                <p className="stat-value text-violet-600">
                  {license_consumption?.active_teams_count || 0}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              of {license_consumption?.contracted_team_slots || 0} slots
            </p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">Remaining Slots</p>
                <p className="stat-value text-emerald-600">
                  {license_consumption?.remaining_slots || 0}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <Layers className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <Progress 
              value={((license_consumption?.remaining_slots || 0) / (license_consumption?.contracted_team_slots || 1)) * 100} 
              className="mt-3 h-1.5"
            />
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">Data Completeness</p>
                <p className="stat-value text-cyan-600">
                  {data_completeness?.org_completion_percentage || 0}%
                </p>
              </div>
              <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900/30">
                <Target className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {data_completeness?.users_with_strengths || 0} of {data_completeness?.total_users || 0} users
            </p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">Balance Score</p>
                <p className="stat-value text-amber-600">
                  {strength_distribution?.balance_score || 0}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Strength diversity index
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Teams Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in stagger-2">
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Team Status</CardTitle>
            <CardDescription>Overview of all teams in your organization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {license_consumption?.teams?.length > 0 ? (
                license_consumption.teams.map((team) => (
                  <div 
                    key={team.team_id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
                  >
                    <div className="flex items-center gap-3">
                      {team.status === 'active' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                      {team.status === 'draft' && <Clock className="w-4 h-4 text-slate-400" />}
                      {team.status === 'archived' && <XCircle className="w-4 h-4 text-amber-500" />}
                      <span className="font-medium text-slate-700 dark:text-slate-300">{team.name}</span>
                    </div>
                    <Badge variant={team.status === 'active' ? 'default' : 'secondary'} className="capitalize">
                      {team.status}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No teams created yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Team Completeness</CardTitle>
            <CardDescription>Strength assignment progress by team</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data_completeness?.team_completeness?.length > 0 ? (
                data_completeness.team_completeness.map((team) => (
                  <div key={team.team_id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {team.name}
                      </span>
                      <span className="text-sm text-slate-500">
                        {team.members_with_strengths}/{team.total_members} ({team.completion_percentage}%)
                      </span>
                    </div>
                    <Progress value={team.completion_percentage} className="h-2" />
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Strength Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in stagger-3">
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Top Strengths</CardTitle>
            <CardDescription>Most common strengths across the organization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {strength_distribution?.top_strengths?.length > 0 ? (
                strength_distribution.top_strengths.map((strength, index) => (
                  <div 
                    key={strength.name}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 text-xs font-medium flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="font-medium text-slate-700 dark:text-slate-300">{strength.name}</span>
                    </div>
                    <Badge variant="secondary">{strength.count}</Badge>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No strength data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Category Distribution</CardTitle>
            <CardDescription>Strengths by category across teams</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {strength_distribution?.category_distribution?.length > 0 ? (
                strength_distribution.category_distribution.map((cat) => (
                  <div key={cat.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {cat.name}
                      </span>
                      <span className="text-sm text-slate-500">
                        {cat.percentage}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-violet-600 to-cyan-500 transition-all duration-500"
                        style={{ width: `${cat.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No category data yet</p>
              )}

              {strength_distribution?.underrepresented_categories?.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">Underrepresented categories:</span>
                  </div>
                  <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
                    {strength_distribution.underrepresented_categories.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Over-indexed Teams */}
      {team_comparison?.over_indexed_teams?.length > 0 && (
        <Card className="animate-fade-in stagger-4">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-600" />
              <CardTitle style={{ fontFamily: 'Manrope' }}>Teams with Dominant Categories</CardTitle>
            </div>
            <CardDescription>Teams where one category represents more than 50% of strengths</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {team_comparison.over_indexed_teams.map((team) => (
                <div 
                  key={team.team_id}
                  className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
                >
                  <p className="font-medium text-slate-700 dark:text-slate-300">{team.name}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {team.dominant_category}: <span className="font-medium text-amber-600">{team.percentage}%</span>
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messaging" className="mt-6">
          <MessagingCenter />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudioPage;
